import React from "react";
import Asset from "../../../models/asset";
import Document from "../../../models/document";
import DocumentCategory from "../../../models/document-category";
import Subcompany from "../../../models/subcompany";
import { requiredFields } from "./constants";
import { getActiveAdditionalFields } from "./additionalFieldColumns";

interface BuildNewDocumentParams {
  additionalFields: any;
  assets: Asset[];
  companyId: number | undefined;
  documentCategories: DocumentCategory[];
  nextId: () => number;
  subcompanies: Subcompany[];
}

export const buildNewSpreadsheetDocument = ({
  additionalFields,
  assets,
  companyId,
  documentCategories,
  nextId,
  subcompanies,
}: BuildNewDocumentParams): Document => {
  const defaultAsset = assets.length > 0 ? assets[0] : undefined;
  const newDoc: any = {
    id: nextId(),
    name: "",
    subcompanyId: subcompanies.length > 0 ? subcompanies[0].id : undefined,
    documentCategoryId:
      documentCategories.length > 0 ? documentCategories[0].id : undefined,
    documentSubcategoryId: undefined,
    documentType: "digital",
    assetId: assets.length > 0 ? assets[0].id : undefined,
    typeName: defaultAsset?.typeName || "",
    physicalCopyRequired: false,
    expirationDate: undefined,
    expirationNoticeDays: 30,
    expirationGraceDays: 15,
    expirationReminderRepeatDays: 7,
    filePath: "",
    companyId,
  };

  getActiveAdditionalFields(additionalFields).forEach((field) => {
    newDoc[`fieldValue${field.index}`] = "";
  });

  return newDoc as Document;
};

export const getSpreadsheetUrlParams = (searchString: string) => {
  const urlParams = new URLSearchParams(searchString);
  let page = urlParams.get("page")
    ? parseInt(urlParams.get("page") as string)
    : 1;

  if (page <= 0) {
    page = 1;
  }

  const search = urlParams.get("search") ? urlParams.get("search") : "";
  const map = new Map();
  map.set("search", search);
  map.set("page", page);

  return map;
};

export const getSpreadsheetRowClass = (
  params: any,
  changedCells: { [key: number]: Set<string> },
) => {
  if (!params.data) return "";

  const classes = [];

  if (changedCells[params.data.id] && changedCells[params.data.id].size > 0) {
    classes.push("row-changed");
  }

  const hasEmptyRequiredField = requiredFields.some(
    (field) =>
      params.data[field.field] === undefined ||
      params.data[field.field] === null ||
      params.data[field.field] === "",
  );

  if (hasEmptyRequiredField) {
    classes.push("row-invalid");
  }

  return classes.join(" ");
};

export const openNativeDatePicker = (
  event: React.SyntheticEvent<HTMLInputElement>,
) => {
  event.stopPropagation();

  const input = event.currentTarget as HTMLInputElement & {
    showPicker?: () => void;
  };

  try {
    input.showPicker?.();
  } catch {
    // Some browsers only allow showPicker during direct user activation.
  }
};

export const setGridDateValue = (
  params: any,
  field: string,
  value: string | null,
) => {
  params.data[field] = value;

  if (params.node?.setDataValue) {
    params.node.setDataValue(field, value);
    return;
  }

  params.setValue(value);
};

export const stopGridMouseEvent = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};
